import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Flex, Typography } from "antd";
import {
  useDelete,
  useInvalidate,
  useOne,
  useTranslate,
} from "@refinedev/core";
import type { MediaProjectResponse } from "pages/media/types";
import {
  DOCUMENT_ACCEPT,
  matchesAccept,
  toDocumentFilePresentationFromProject,
} from "../../helpers/fileList";
import { useAddProjectDocument } from "pages/media/projects/hooks/useAddProjectDocument";
import { PlaybookAddOnCard } from "../PlaybookAddOnCard";
import { CONFIG_ITEM } from "../../data/addons";
import { DocumentFile } from "./types";
import { AddDocumentButton } from "./documents/AddDocumentButton";
import { DocumentEditRow } from "./documents/DocumentEditRow";
import { DocumentPreviewModal } from "./documents/DocumentPreviewModal";

/**
 * Documents card for the Edit Experience page (MED-6970).
 *
 * Unlike the create-flow `DocumentsConfigCard` (which reads/writes the
 * PlaybookDraft context), an existing project's documents are fetched
 * straight from the project itself and mutated one at a time — there's no
 * draft to reconcile. Mirrors `VideoEditCard`'s pattern: its own data
 * fetching, no PlaybookDraft dependency.
 *
 * Source files are fixed on edit (no visibility icon, no delete — they're
 * what the experience was generated from). Extras is the only editable list:
 * Preview + Delete per row, and the single "Add a document" button always
 * adds there (`hidden: true`) — there's no toggle on this page to move a
 * document between the two lists.
 */
export const DocumentsEditCard: React.FC = () => {
  const t = useTranslate();
  const { projectId } = useParams<{ projectId: string }>();
  const [previewFile, setPreviewFile] = useState<DocumentFile | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data } = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: projectId ?? "",
    queryOptions: { enabled: Boolean(projectId) },
  });
  const documents = data?.data?.documents ?? [];
  const sourceDocs = documents.filter((doc) => !doc.hidden);
  const extraDocs = documents.filter((doc) => doc.hidden);

  const invalidate = useInvalidate();
  const invalidateProject = () =>
    invalidate({
      resource: "media/projects",
      id: projectId,
      invalidates: ["detail"],
    });

  const { mutateAsync: deleteDocument } = useDelete();
  const handleDelete = async (documentId: string) => {
    if (!projectId) return;
    setDeletingId(documentId);
    try {
      await deleteDocument({
        resource: `media/projects/${projectId}/documents`,
        id: documentId,
      });
      await invalidateProject();
    } finally {
      setDeletingId(null);
    }
  };

  const { addProjectDocument } = useAddProjectDocument(projectId ?? "");
  const [adding, setAdding] = useState(false);
  const handleAddFiles = async (list: FileList | null) => {
    if (!list || list.length === 0 || !projectId) return;
    const accepted = Array.from(list).filter((file) =>
      matchesAccept(file, DOCUMENT_ACCEPT)
    );
    if (accepted.length === 0) return;
    setAdding(true);
    try {
      await Promise.all(accepted.map((file) => addProjectDocument(file)));
      await invalidateProject();
    } finally {
      setAdding(false);
    }
  };

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Documents}
      title={t("createExperience.addOns.documents.title")}
      description={t("createExperience.addOns.documents.description")}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={DOCUMENT_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => {
          void handleAddFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <Typography.Text strong>
        {t("createExperience.addOns.documents.sourceFiles.title")}
      </Typography.Text>
      <Typography.Paragraph
        type="secondary"
        style={{ margin: "4px 0 16px", lineHeight: 1.45 }}
      >
        {t("createExperience.addOns.documents.sourceFiles.fixedDesc", {
          count: sourceDocs.length,
        })}
      </Typography.Paragraph>
      <Flex vertical gap={10}>
        {sourceDocs.map((doc) => (
          <DocumentEditRow
            key={doc.id}
            file={toDocumentFilePresentationFromProject(doc)}
            onPreview={() =>
              setPreviewFile(toDocumentFilePresentationFromProject(doc))
            }
          />
        ))}
      </Flex>

      <Typography.Text strong style={{ display: "block", marginTop: 24 }}>
        {t("createExperience.addOns.documents.extras.title")}
      </Typography.Text>
      <Typography.Paragraph
        type="secondary"
        style={{ margin: "4px 0 16px", lineHeight: 1.45 }}
      >
        {t("createExperience.addOns.documents.extras.editDesc", {
          count: extraDocs.length,
        })}
      </Typography.Paragraph>
      {extraDocs.length > 0 ? (
        <Flex vertical gap={10}>
          {extraDocs.map((doc) => (
            <DocumentEditRow
              key={doc.id}
              file={toDocumentFilePresentationFromProject(doc)}
              onPreview={() =>
                setPreviewFile(toDocumentFilePresentationFromProject(doc))
              }
              onDelete={() => void handleDelete(doc.id)}
              deleting={deletingId === doc.id}
            />
          ))}
        </Flex>
      ) : null}

      <AddDocumentButton
        label={t("createExperience.addOns.documents.addMore")}
        loading={adding}
        onClick={() => fileInputRef.current?.click()}
      />

      <DocumentPreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </PlaybookAddOnCard>
  );
};

export default DocumentsEditCard;
